import { forwardRef, useContext } from "react";
import {
  TitleContext,
  TitleDescriptionContext,
} from "@common/context/ThemeContext";
import { SectionProps } from "@common/types";
import { cn } from "@app/lib/utils";
import {
  CardContent,
  CardHeader,
  cardContentPadding,
  cardHeaderClassName,
  cardSurfaceClassName,
} from "./Card";

export const Section = forwardRef<
  HTMLDivElement,
  SectionProps & { containerClassName?: string } & Omit<
      React.HTMLAttributes<HTMLDivElement>,
      "title"
    >
>(function Section(
  {
    title,
    subtitle,
    className,
    children,
    icon,
    padding = "medium",
    action,
    containerClassName,
    pure,
    hasBackground = true,
    ...rest
  },
  ref,
) {
  const pageTitle = useContext(TitleContext);
  const pageDescription = useContext(TitleDescriptionContext);
  const titleParts =
    typeof title === "string"
      ? title.match(/^([0-9\u06F0-\u06F9]+)\.\s*(.*)$/)
      : null;
  const titleNode = titleParts ? titleParts[2] : title;
  const hasEnhancedHeader = Boolean(icon || titleParts);
  const normalizedTitle =
    typeof titleNode === "string" ? titleNode.trim() : undefined;
  const isDuplicatedPageHeader =
    Boolean(normalizedTitle) &&
    (normalizedTitle === pageTitle?.trim() ||
      normalizedTitle === pageDescription?.trim());
  const sectionHeaderProps = {
    "data-section-header": "true",
    "data-section-simple-title":
      typeof title === "string" && !subtitle && !action && !hasEnhancedHeader
        ? "true"
        : undefined,
  } as const;

  const surfaceClassName = cn(
    pure !== true && cardSurfaceClassName,
    pure === true && "text-card-foreground",
    !hasBackground && "bg-transparent",
    className,
  );

  return (
    <div
      data-section-root="true"
      data-section-surface={pure !== true ? "true" : undefined}
      className={surfaceClassName}
      ref={ref}
      {...rest}
    >
      {title &&
        !isDuplicatedPageHeader &&
        (hasEnhancedHeader ? (
          <div className={cardHeaderClassName} {...sectionHeaderProps}>
            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between md:gap-4">
              <div
                className={cn(
                  "min-w-0 flex-1 text-start",
                  icon
                    ? "grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3"
                    : "flex flex-col",
                )}
              >
                {icon && <StepHeaderMark>{icon}</StepHeaderMark>}
                <div className="min-w-0 flex-1 text-start">
                  <h3 className="truncate text-start text-sm font-bold leading-6 text-foreground md:text-base md:leading-7">
                    {titleNode}
                  </h3>
                  {subtitle && (
                    <p className="mt-0.5 text-start text-xs leading-4 text-muted-foreground md:text-sm md:leading-5">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              {action && (
                <div className="w-full shrink-0 sm:w-auto">{action}</div>
              )}
            </div>
          </div>
        ) : pure ? (
          <CardHeader
            title={titleNode}
            description={subtitle}
            action={action}
            {...sectionHeaderProps}
          />
        ) : (
          <CardHeader
            title={titleNode}
            description={subtitle}
            action={action}
            {...sectionHeaderProps}
          />
        ))}
      {pure !== true ? (
        <CardContent
          className={cn(containerClassName, cardContentPadding[padding])}
          padding="none"
        >
          {children}
        </CardContent>
      ) : (
        children
      )}
    </div>
  );
});

export function StepHeaderMark({ children }: { children: React.ReactNode }) {
  return (
    <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/10 md:h-10 md:w-10 [&_svg]:h-5 [&_svg]:w-5 [&_svg]:stroke-[2]">
      {children}
    </span>
  );
}
